import { queryClient } from '@/util/queryClient'
import Toasts from '@/util/toasts'
import { useQuery } from '@tanstack/vue-query'
import { watch } from 'vue'

const versionCheckPath = '/version.json'
const pollingMS = 60000
const reloadDelayMS = 5000

/**
 * Watches for a new build having been deployed since this tab loaded, and
 * reloads the tab when it finds one.
 *
 * The version *deployed* on the server is polled from the json file at
 * `versionCheckPath`, which for now is the file at `public/version.json`.
 * Build commands all write to that file via `npm run write-version`. It is
 * compared against `import.meta.env.PACKAGE_VERSION`, the version baked into
 * the bundle we are *running*. Both sides are needed: a compile-time constant
 * can't tell us what the server is serving now, and the polled value can't
 * tell us what this tab is executing.
 */
const useVersionCheck = () => {
  // Nothing can be deployed under a dev server, so no need to poll.
  if (import.meta.env.DEV) return

  const { data: deployedVersion } = useQuery(
    {
      queryKey: ['appVersion'],
      queryFn: async () => {
        const res = await fetch(versionCheckPath)
        return (await res.json()).version as string
      },
      refetchInterval: pollingMS,
    },
    queryClient,
  )

  watch(deployedVersion, (version) => {
    if (version && version !== import.meta.env.PACKAGE_VERSION) {
      Toasts.Info(
        'Application reloading',
        `A new version of this application is available. Your page will reload within ${Math.floor(reloadDelayMS / 1000)} seconds.`,
      )
      setTimeout(() => window.location.reload(), reloadDelayMS)
    }
  })
}

export default useVersionCheck
